import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseKey) {
  throw new Error(
    'Missing Supabase environment variables.\n' +
    'Ensure REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY are set in your .env.local file (local) ' +
    'or in your hosting platform\'s environment variable settings (production).'
  )
}

export const supabase = createClient(supabaseUrl, supabaseKey)

// Helper functions for vehicle inspection operations

// Create a new inspection
export const createInspection = async (inspectionData) => {
  const { data: { user } } = await supabase.auth.getUser();

  // Get org_id from user profile
  const { data: profileData } = await supabase
    .from('user_profiles')
    .select('org_id')
    .eq('id', user?.id)
    .single();

  const { data, error } = await supabase
    .from('vehicle_inspections')
    .insert({
      vin: inspectionData.vin,
      make: inspectionData.make,
      model: inspectionData.model,
      year: inspectionData.year,
      asset_id: inspectionData.assetId,
      odometer: inspectionData.odometer,
      location: inspectionData.location,
      status: 'in_progress',
      inspector_id: user?.id ?? null,
      org_id: profileData?.org_id ?? null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating inspection:', error);
    throw error;
  }

  return data;
};

// Update inspection data
export const updateInspection = async (id, updates) => {
  const { data, error } = await supabase
    .from('vehicle_inspections')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating inspection:', error);
    throw error;
  }

  return data;
};

// Get inspection by ID
export const getInspection = async (id) => {
  const { data, error } = await supabase
    .from('vehicle_inspections')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching inspection:', error);
    throw error;
  }

  return data;
};

// Upload photo to Supabase storage
export const uploadPhoto = async (file, path) => {
  const { data, error } = await supabase.storage
    .from('inspection-photos')
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false
    });

  if (error) {
    console.error('Error uploading photo:', error);
    throw error;
  }

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from('inspection-photos')
    .getPublicUrl(data.path);

  return publicUrl;
};

// Save photo record to database
export const savePhotoRecord = async (inspectionId, category, fileUrl, fileName) => {
  // Get org_id for this inspection
  const { data: inspData } = await supabase
    .from('vehicle_inspections')
    .select('org_id')
    .eq('id', inspectionId)
    .single();

  const { data, error } = await supabase
    .from('inspection_photos')
    .insert({
      inspection_id: inspectionId,
      category: category,
      file_url: fileUrl,
      file_name: fileName,
      org_id: inspData?.org_id ?? null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error saving photo record:', error);
    throw error;
  }

  return data;
};

// Get all inspections (for dashboard/list view)
export const getInspections = async (limit = 50) => {
  const { data, error } = await supabase
    .from('vehicle_inspections')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching inspections:', error);
    throw error;
  }

  return data;
};

// Sign in with email/password
export const signIn = (email, password) =>
  supabase.auth.signInWithPassword({ email, password });

// Sign out
export const signOut = () => supabase.auth.signOut();

// Get current session user
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// ── Audit Trail ───────────────────────────────────────────────────────────────
// Direct writes are replaced by the write-ahead queue in auditQueue.js
// This re-exports enqueueAuditEvent as logAuditEvent so all call sites
// remain unchanged — they just queue instead of writing directly.

export { enqueueAuditEvent as logAuditEvent } from './auditQueue';
export { readQueue as getFailedAuditEvents, getPendingCount } from './auditQueue';
